import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  useTestimonials,
  useCreateTestimonial,
  useUpdateTestimonial,
  useDeleteTestimonial,
} from '../hooks/useTestimonials';
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff, Star } from 'lucide-react';
import { PageHeader, ResponsiveTable } from '../components/ui/responsive-table';
import type { Testimonial } from '../types/entities';

const testimonialSchema = z.object({
  author: z.string().min(1, 'Auteur requis'),
  service: z.string().nullable().optional(),
  rating: z.number().int('La note doit être un entier').min(1, 'La note doit être entre 1 et 5').max(5, 'La note doit être entre 1 et 5'),
  content: z.string().min(10, 'Contenu requis (minimum 10 caractères)'),
  published: z.boolean(),
});

type TestimonialFormData = z.infer<typeof testimonialSchema>;

export default function Testimonials() {
  const { data: testimonials, isLoading } = useTestimonials();
  const createTestimonial = useCreateTestimonial();
  const updateTestimonial = useUpdateTestimonial();
  const deleteTestimonial = useDeleteTestimonial();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<TestimonialFormData>({
    resolver: zodResolver(testimonialSchema),
    defaultValues: {
      author: '',
      service: null,
      rating: 5,
      content: '',
      published: false,
    },
  });

  const handleOpenCreate = () => {
    reset({
      author: '',
      service: null,
      rating: 5,
      content: '',
      published: false,
    });
    setEditingTestimonial(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (testimonial: Testimonial) => {
    reset({
      author: testimonial.author,
      service: testimonial.service,
      rating: testimonial.rating,
      content: testimonial.content,
      published: testimonial.published,
    });
    setEditingTestimonial(testimonial);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    await deleteTestimonial.mutateAsync(id);
    setDeleteConfirm(null);
  };

  const onSubmit = async (data: TestimonialFormData) => {
    if (editingTestimonial) {
      await updateTestimonial.mutateAsync({ id: editingTestimonial.id, data });
    } else {
      await createTestimonial.mutateAsync(data);
    }
    setIsModalOpen(false);
  };

  const togglePublished = async (testimonial: Testimonial) => {
    await updateTestimonial.mutateAsync({
      id: testimonial.id,
      data: { published: !testimonial.published },
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Témoignages"
          description="Gérez les témoignages clients"
          action={
            <Button onClick={handleOpenCreate} className="gap-2 w-full sm:w-auto">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nouveau témoignage</span>
              <span className="sm:hidden">Nouveau</span>
            </Button>
          }
        />

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Mobile/Tablet: Card Layout */}
            <div className="lg:hidden space-y-4">
              {testimonials?.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="p-4">
                    {/* Header with author and rating */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {testimonial.author}
                        </h3>
                        {testimonial.service && (
                          <p className="text-sm text-gray-500">{testimonial.service}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {Array.from({ length: testimonial.rating }).map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    </div>

                    {/* Content */}
                    <p className="text-sm text-gray-600 line-clamp-3 mb-3">
                      {testimonial.content}
                    </p>

                    {/* Status badge */}
                    <div className="mb-4">
                      <button
                        onClick={() => togglePublished(testimonial)}
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          testimonial.published
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {testimonial.published ? (
                          <>
                            <Eye className="w-3 h-3 mr-1" />
                            Publié
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-3 h-3 mr-1" />
                            Brouillon
                          </>
                        )}
                      </button>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenEdit(testimonial)}
                        className="flex-1 sm:flex-none"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                      {deleteConfirm === testimonial.id ? (
                        <>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(testimonial.id)}
                          >
                            Confirmer
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            Annuler
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteConfirm(testimonial.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Table Layout */}
            <div className="hidden lg:block">
              <ResponsiveTable>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Auteur
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Service
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Note
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Contenu
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {testimonials?.map((testimonial) => (
                      <tr key={testimonial.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {testimonial.author}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {testimonial.service || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="flex items-center gap-1">
                            {Array.from({ length: testimonial.rating }).map((_, i) => (
                              <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-md">
                          <div className="truncate">{testimonial.content}</div>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => togglePublished(testimonial)}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                              testimonial.published
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {testimonial.published ? (
                              <>
                                <Eye className="w-3 h-3 mr-1" />
                                Publié
                              </>
                            ) : (
                              <>
                                <EyeOff className="w-3 h-3 mr-1" />
                                Brouillon
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(testimonial)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {deleteConfirm === testimonial.id ? (
                            <div className="inline-flex gap-1">
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDelete(testimonial.id)}
                              >
                                Confirmer
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteConfirm(null)}
                              >
                                Annuler
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirm(testimonial.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ResponsiveTable>
            </div>
          </>
        )}
      </div>

      {/* Modal Form */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b">
              <h2 className="text-lg sm:text-xl font-semibold">
                {editingTestimonial ? 'Modifier le témoignage' : 'Nouveau témoignage'}
              </h2>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="author">Auteur *</Label>
                <Input
                  id="author"
                  {...register('author')}
                  className={errors.author ? 'border-red-500' : ''}
                  placeholder="Ex: Marie L."
                />
                {errors.author && (
                  <p className="text-sm text-red-600">{errors.author.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="service">Service</Label>
                <Input
                  id="service"
                  {...register('service')}
                  placeholder="Ex: Massage Relaxant"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rating">Note *</Label>
                <Select
                  value={watch('rating')?.toString() || '5'}
                  onValueChange={(value) => setValue('rating', parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez une note" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">⭐⭐⭐⭐⭐ (5/5)</SelectItem>
                    <SelectItem value="4">⭐⭐⭐⭐ (4/5)</SelectItem>
                    <SelectItem value="3">⭐⭐⭐ (3/5)</SelectItem>
                    <SelectItem value="2">⭐⭐ (2/5)</SelectItem>
                    <SelectItem value="1">⭐ (1/5)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Contenu *</Label>
                <Textarea
                  id="content"
                  {...register('content')}
                  rows={5}
                  className={errors.content ? 'border-red-500' : ''}
                  placeholder="Le témoignage complet du client..."
                />
                {errors.content && (
                  <p className="text-sm text-red-600">{errors.content.message}</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="published"
                  {...register('published')}
                  className="w-4 h-4 text-spa-gold border-gray-300 rounded focus:ring-spa-gold"
                />
                <Label htmlFor="published" className="cursor-pointer">
                  Publier ce témoignage
                </Label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                >
                  Annuler
                </Button>
                <Button type="submit">
                  {editingTestimonial ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
